import { Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  PowerFlowGraphMode,
  System,
  SystemEnergyState,
  customerPortalTelemetryStyles,
  isNotNullOrUndefined,
} from 'ngx-core-services';
import { map, switchMap, tap } from 'rxjs/operators';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { SystemsState, requestSystemEnergyState, selectedSystemStructure, selectedSystemUid } from '../../store';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { selectSystemEnergyStateBySelectedSystem, selectSystemTotalizerStateBySelectedSystem } from '../../store/selectors/totalizer.selectors';

const getLabels = () => ({
  title: 'System totalizer',
  chargedEnergy: 'Total charged energy',
  consumedEnergy: 'Total consumed energy',
  dischargedEnergy: 'Total discharged energy',
  exportedEnergy: 'Total exported energy',
  generatedEnergy: 'Total generated energy',
  importedEnergy: 'Total imported energy',
  loading: 'Loading ...',
  kilometersOfCarTravelSaved: 'Kilometers of car travel saved',
  treeNeededToOffsetCO2: 'Trees needed to offset CO2',
  KilogramsOfCO2Saved: 'Kilograms of CO2 saved'
});

/**
 * @description A tree can absorb 22 kg of CO2 per year
 */
const CO2_PER_TREE_PER_YEAR = 22;

/**
 * @description A car emits 0.12 kg of CO2 per km
 */
const CO2_PER_KM = 0.12;

/**
 * @description 0.4 kg of CO2 saved per kWh
 */
const CO2_SAVED_PER_KWH = 0.4;

const getTreePlanted = (energyGenerated: number) => {
  return Math.ceil(energyGenerated * CO2_SAVED_PER_KWH / CO2_PER_TREE_PER_YEAR);
};

const getKmSaved = (energyGenerated: number) => {
  return Math.ceil((energyGenerated * CO2_SAVED_PER_KWH) / CO2_PER_KM);
};

const getCO2Saved = (energyGenerated: number) => {
  return Math.ceil(energyGenerated * CO2_SAVED_PER_KWH);
};

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-1000': 1000,
  'breakpoint-550': 550,
})
@Component({
  selector: 'lib-system-totalizer',
  templateUrl: './system-totalizer.component.html',
  styleUrls: ['./system-totalizer.component.scss'],
})
export class SystemTotalizerComponent implements ICustomBlock, IReduxBlock {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  @Input() locate: string = 'it-IT';

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _instanceId$: Observable<string> = new Observable<string>();
  readonly totalizerSelector$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(selectSystemEnergyStateBySelectedSystem(instanceId))),
  );

  readonly requestTotalizers$ = combineLatest([
    this._refresh$,
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this._instanceId$,
  ]).pipe(
    tap(([forceRefresh, systemId, instanceId]) =>
      this.store.dispatch(requestSystemEnergyState({ systemId, instanceId, forceRefresh }),
    )
  ));

  readonly totalizers$ = combineLatest([
    this.totalizerSelector$,
    this.store.select(selectedSystemStructure),
  ]).pipe(
    map(([data, structure]) => data && (structure !== undefined && structure !== null) ? ({
      energies: this.adaptEnergyToOverview(data, structure),
      treePlanted: this.getTreePlanted(data.energyTotalGenerating),
      kmSaved: this.getKmSaved(data.energyTotalGenerating),
      co2Saved: this.getCO2Saved(data.energyTotalGenerating),
    }) : null),
  );

  readonly requestIsReady$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(selectSystemTotalizerStateBySelectedSystem(instanceId)).pipe(
      isNotNullOrUndefined(),
      map((status) => status === 'ready'),
    )),
  );

  PowerFlowGraphMode = PowerFlowGraphMode;
  readonly getCO2Saved = getCO2Saved;
  readonly getKmSaved = getKmSaved;
  readonly getTreePlanted = getTreePlanted;

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
  ) { }

  adaptEnergyToOverview(data: SystemEnergyState, systemType: PowerFlowGraphMode) {

    const generationEnergy = {
      name: 'generatedEnergy' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('ET1').bgColor,
      value: Number(data.energyTotalGenerating || 0)
    };

    const consumeEnergy = {
      name: 'consumedEnergy' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('ET4').bgColor,
      value: Number(data.energyTotalConsuming || 0)
    };

    const dischargedEnergy = {
      name: 'dischargedEnergy' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('ET6').bgColor,
      value: Number(data.energyTotalDischarging || 0)
    };

    const chargedEnergy = {
      name: 'chargedEnergy' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('ET5').bgColor,
      value: Number(data.energyTotalCharging || 0)
    };

    const importedEnergy = {
      name: 'importedEnergy' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('ET3').bgColor,
      value: Number(data.energyTotalImporting || 0)
    };

    const exportedEnergy = {
      name: 'exportedEnergy' as keyof typeof this.labels,
      bgColor: customerPortalTelemetryStyles('ET2').bgColor,
      value: Number(data.energyTotalExporting || 0)
    };

    switch (systemType) {
      case PowerFlowGraphMode.OnlyGeneratedPower:
        return [generationEnergy];
      case PowerFlowGraphMode.WithImportExport:
        return [generationEnergy, consumeEnergy, importedEnergy, exportedEnergy];
      case PowerFlowGraphMode.WithBattery:
        return [generationEnergy, consumeEnergy, dischargedEnergy, chargedEnergy, importedEnergy, exportedEnergy];
      default:
        return [];
    }
  }
}
